import React, { useState } from "react";
import { adminApi } from "../../services/api";
import { formatEnglishDate } from "../../utils/dateFormatting";

/**
 * Componente per certificare una richiesta ESG OFF-CHAIN (senza blockchain)
 * Per aziende senza wallet o certificazioni interne
 * 
 * Flow:
 * 1. Call backend POST /admin/esg/certifications/prepare-proxy
 * 2. Show confirmation dialog with certification details
 * 3. Confirm WITHOUT blockchain transaction
 * 4. Call backend POST /admin/esg/certifications/confirm-offchain
 * 5. Update UI state
 */
const AdminCertifyOffChainButton = ({ 
  requestId, 
  adminKey, 
  walletAddress,
  onSuccess,
  onError 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [certData, setCertData] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  const handlePrepare = async () => {
    setLoading(true);
    setError("");
    
    try {
      console.log("🔧 Preparing OFF-CHAIN certification for:", requestId);
      const data = await adminApi.prepareCertification(requestId, adminKey, walletAddress);
      
      console.log("✅ Certification data prepared:", data);
      setCertData(data);
      setShowDialog(true);
    } catch (err) {
      console.error("❌ Prepare error:", err);
      setError(err.message || "Failed to prepare certification");
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOffChain = async () => {
    if (!certData) return;
    
    setLoading(true);
    setError("");
    
    try {
      console.log("💾 Confirming OFF-CHAIN certification...");
      
      // Call off-chain confirm endpoint (senza txHash)
      const result = await adminApi.confirmCertificationOffChain(
        requestId,
        certData.reportCID,
        certData.pdfCID,
        adminKey,
        walletAddress
      );
      
      console.log("✅ OFF-CHAIN certification confirmed:", result);
      
      setShowDialog(false);
      if (onSuccess) onSuccess(result);
    } catch (err) {
      console.error("❌ Confirm error:", err);
      setError(err.message || "Failed to confirm certification");
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handlePrepare}
        disabled={loading}
        style={{
          ...styles.certifyButton,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        {loading ? "⏳ Processing..." : "📋 Certify Off-Chain (No Wallet)"}
      </button>

      {error && <div style={styles.error}>{error}</div>}

      {showDialog && certData && (
        <div style={styles.dialogOverlay} onClick={() => setShowDialog(false)}>
          <div style={styles.dialogBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.dialogTitle}>⚠️ Confirm OFF-CHAIN Certification</h3>
            
            <div style={styles.warningBox}>
              <strong>⚠️ Off-Chain Mode</strong>
              <p>This certification will be saved in the database but NOT recorded on blockchain.</p>
              <p>Use this for companies without a wallet or for internal certifications.</p>
            </div>

            <div style={styles.dialogContent}>
              <div style={styles.dialogRow}>
                <strong>Company:</strong>
                <span>{certData.companyName}</span>
              </div>
              <div style={styles.dialogRow}>
                <strong>Submission ID:</strong>
                <span style={styles.address}>{certData.submissionId}</span>
              </div>
              <div style={styles.dialogRow}>
                <strong>Report IPFS CID:</strong>
                <span style={styles.address}>{certData.reportCID}</span>
              </div>
              <div style={styles.dialogRow}>
                <strong>PDF IPFS CID:</strong>
                <span style={styles.address}>{certData.pdfCID}</span>
              </div>
              {certData.ipfsGatewayUrls && (
                <>
                  <div style={styles.dialogRow}>
                    <strong>JSON Report:</strong>
                    <a 
                      href={certData.ipfsGatewayUrls.json} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={styles.link}
                    >
                      View JSON
                    </a>
                  </div>
                  <div style={styles.dialogRow}>
                    <strong>PDF Report:</strong>
                    <a 
                      href={certData.ipfsGatewayUrls.pdf} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={styles.link}
                    >
                      View PDF
                    </a>
                  </div>
                </>
              )}
              <div style={styles.dialogRow}>
                <strong>Issued:</strong>
                <span>{formatEnglishDate(certData.issuedAt)}</span>
              </div>
              <div style={styles.dialogRow}>
                <strong>Expires:</strong>
                <span>{formatEnglishDate(certData.expiresAt)}</span>
              </div>
            </div>

            <div style={styles.dialogActions}>
              <button
                onClick={() => setShowDialog(false)}
                disabled={loading}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmOffChain}
                disabled={loading}
                style={styles.confirmButton}
              >
                {loading ? "⏳ Saving..." : "✓ Confirm Off-Chain Certification"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  certifyButton: {
    padding: "12px 24px",
    fontSize: "1rem",
    fontWeight: 600,
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
  },
  error: {
    marginTop: 12,
    padding: 12,
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: 8,
    fontSize: "0.9rem",
  },
  dialogOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
  },
  dialogBox: {
    background: "#ffffff",
    borderRadius: 16,
    padding: 32,
    maxWidth: 600,
    width: "90%",
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  },
  dialogTitle: {
    margin: "0 0 20px 0",
    fontSize: "1.5rem",
    color: "#0f172a",
  },
  warningBox: {
    background: "#fef3c7",
    border: "2px solid #f59e0b",
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  dialogContent: {
    marginBottom: 24,
  },
  dialogRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 0",
    borderBottom: "1px solid #e2e8f0",
    gap: 16,
  },
  address: {
    fontFamily: "monospace",
    fontSize: "0.85rem",
    color: "#475569",
    wordBreak: "break-all",
  },
  link: {
    color: "#3b82f6",
    textDecoration: "none",
    fontWeight: 600,
  },
  dialogActions: {
    display: "flex",
    gap: 12,
    justifyContent: "flex-end",
  },
  cancelButton: {
    padding: "10px 20px",
    fontSize: "1rem",
    fontWeight: 600,
    color: "#64748b",
    background: "#f1f5f9",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },
  confirmButton: {
    padding: "10px 20px",
    fontSize: "1rem",
    fontWeight: 600,
    color: "#ffffff",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },
};

export default AdminCertifyOffChainButton;
